import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

type Callback = () => void

const useInterval = (callback: Callback, delay: number) => {
	const savedCallback = useRef<Callback | undefined>(undefined)

	useEffect(() => {
		savedCallback.current = callback
	}, [callback])

	useEffect(() => {
		function tick() {
			savedCallback && savedCallback.current && savedCallback.current()
		}
		if (delay !== null) {
			const id = setInterval(tick, delay)
			return () => clearInterval(id)
		}
	}, [delay])
}

function useStateParams<T>(initialState: T, paramsName: string, serialize: (state: T) => string, deserialize: (state: string) => T): [T, (state: T) => void] {
	const location = useLocation()
	const navigate = useNavigate()
	const search = new URLSearchParams(location.search)

	const existingValue = search.get(paramsName)
	const [currentInitialState, setCurrentInitialState] = useState<T>(initialState)
	const [state, setState] = useState<T>(existingValue ? deserialize(existingValue) : initialState)

	// Update initial state if one has not been provided until now
	// Using this so it doesn't overwrite existing values
	useEffect(() => {
		if (currentInitialState == null) {
			setCurrentInitialState(initialState)
			if (!state) {
				setState(initialState)
			}
		}
	}, [initialState])

	useEffect(() => {
		// Updates state when user navigates backwards or forwards in browser history
		if (existingValue && deserialize(existingValue) !== state) {
			setState(deserialize(existingValue))
		}
	}, [existingValue])

	const onChange = (s: T) => {
		setState(s)
		const searchParams = new URLSearchParams(location.search)
		if (!s) {
			searchParams.delete(paramsName)
		} else {
			searchParams.set(paramsName, serialize(s))
		}
		const pathname = location.pathname
		navigate({ pathname, search: searchParams.toString() })
	}

	return [state, onChange]
}

const useDebounce = <T>(value: T, delay: number) => {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay)
		return () => clearTimeout(timer)
	}, [value])

	return debouncedValue
}

export { useInterval, useStateParams, useDebounce }
